import React, {
  useEffect,
  useState,
} from 'react';
import EmailOptInIcon from '@material-ui/icons/MailOutline';
import TextOptOutIcon from '@material-ui/icons/MobileOff';
import TextOptInIcon from '@material-ui/icons/PhoneAndroid';
import EmailOptOutIcon from '@material-ui/icons/Unsubscribe';
import styled from 'styled-components';

import { FormRadioInput } from '@/core/components/FormRadioInput';
import {
  OptInOptions,
} from '@/core/interfaces/common';

import { FormRadioInputWrapper } from '@/themes/globalStyles';

const ICON_SIZE = 18;

// Mirrors CheckboxWrapper's flex layout so icon-prefixed text aligns with checkbox rows
const ChannelTextRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 10px;

  svg {
    flex-shrink: 0;
    margin-right: 11px;
  }
`;

interface OptCheckboxesProps {
  backgroundColor: string;
  isOptInEmail: boolean;
  isOptInText: boolean;
  handleOptChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  organizationName: string;
  optIn: OptInOptions;
}

export const OptCheckboxes = ({
  backgroundColor,
  isOptInEmail,
  isOptInText,
  handleOptChange,
  organizationName,
  optIn,
}: OptCheckboxesProps) => {
  const emailIsNull = optIn.email.optIn === null;
  const textIsNull = optIn.textSms.optIn === null;

  // isChecked seeds the default checked value, but only for channels that
  // render a checkbox (optIn null); after mount the user's choices win
  const [
    checkedValues,
    setCheckedValues,
  ] = useState({
    isOptInEmail: emailIsNull ? optIn.email.isChecked ?? isOptInEmail : isOptInEmail,
    isOptInText: textIsNull ? optIn.textSms.isChecked ?? isOptInText : isOptInText,
  });

  useEffect(() => {
    if (optIn.defaultOptIn !== null) {
      return;
    }

    // Push seeded defaults into the parent form state so submission matches
    // what is displayed; parent handlers only read event.target.name/checked
    ([
      [
        'isOptInEmail',
        isOptInEmail,
      ],
      [
        'isOptInText',
        isOptInText,
      ],
    ] as const).forEach(([
      name,
      propValue,
    ]) => {
      if (checkedValues[name] !== propValue) {
        handleOptChange({
          target: {
            checked: checkedValues[name],
            name,
          },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked,
    } = event.target;

    setCheckedValues(prevValues => ({
      ...prevValues,
      [name]: checked,
    }));
    handleOptChange(event);
  };

  if (optIn.defaultOptIn !== null) {
    const defaultOptInText = `By submitting this form, you agree to receive future communications from us.
      You can unsubscribe at any time by following the opt-out instructions provided
      in subsequent communications.
      If you have already unsubscribed, submitting this form will not resubscribe you.`;
    const defaultOptOutText = `By submitting this form, you will not be opted in
      to receive further communications from us.`;
    const defaultText = optIn.defaultOptIn ? defaultOptInText : defaultOptOutText;

    return (
      <div className="muster-optin">
        <p>{optIn.defaultOptInText || defaultText}</p>
      </div>
    );
  }

  const defaultEmailLabel = optIn.emailCheckboxText || `Yes, opt-in to email updates from ${organizationName}`;
  const defaultTextLabel = optIn.textCheckboxText || `Yes, opt-in to occasional SMS updates from ${organizationName}`;

  const defaultEmailOptInText = `You are opting-in to future emails from ${organizationName}.
      If you have already unsubscribed, submitting this form will not resubscribe you.
      You can unsubscribe at any time.`;
  const defaultEmailOptOutText = `Submitting this form will NOT opt you in to email from ${organizationName}.`;
  const defaultTextOptInText = `By submitting this form, you agree to receive future text/SMS communications from us.
      If you have already unsubscribed, submitting this form will not resubscribe you.
      You can unsubscribe at any time by replying STOP.`;
  const defaultTextOptOutText = `Submitting this form will NOT opt you in to text/SMS from ${organizationName}.`;

  // Custom text copy that already spells out the opt-out instructions makes the
  // standard disclaimer redundant; the checkbox branch shows checkboxText while
  // the static branch shows optInText, so either one can carry the language
  const textHasStopLanguage = [
    optIn.textSms.optInText,
    optIn.textSms.checkboxText,
  ].some(text => (text || '').includes('STOP to unsubscribe'));

  const emailItem = {
    checked: checkedValues.isOptInEmail,
    icon: optIn.email.optIn === false ? EmailOptOutIcon : EmailOptInIcon,
    inputName: 'isOptInEmail',
    isNull: emailIsNull,
    key: 'email',
    label: optIn.email.checkboxText || defaultEmailLabel,
    showDisclaimer: false,
    staticText: optIn.email.optInText || (
      optIn.email.optIn === true ? defaultEmailOptInText : defaultEmailOptOutText
    ),
  };
  const textItem = {
    checked: checkedValues.isOptInText,
    icon: optIn.textSms.optIn === false ? TextOptOutIcon : TextOptInIcon,
    inputName: 'isOptInText',
    isNull: textIsNull,
    key: 'textSms',
    label: optIn.textSms.checkboxText || defaultTextLabel,
    showDisclaimer: !textHasStopLanguage &&
      (optIn.textSms.optIn === null || optIn.textSms.optIn === true),
    staticText: optIn.textSms.optInText || (
      optIn.textSms.optIn === true ? defaultTextOptInText : defaultTextOptOutText
    ),
  };

  // Null-optIn channels render first; equal nullity preserves email-first order
  const orderedItems = emailIsNull === textIsNull || emailIsNull ?
    [
      emailItem,
      textItem,
    ] :
    [
      textItem,
      emailItem,
    ];

  return (
    <div
      className="muster-optin"
      style={{
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: '450px',
      }}
    >
      {orderedItems.map(item => {
        if (item.isNull) {
          return (
            <FormRadioInputWrapper key={item.key}>
              <FormRadioInput
                checked={item.checked}
                color={backgroundColor}
                label={item.label}
                name={item.inputName}
                onChange={handleChange}
              />
              {item.showDisclaimer && (
                <p style={{ paddingLeft: '40px' }}>
                  Message &amp; data rates may apply. Reply STOP to unsubscribe at any time.
                </p>
              )}
            </FormRadioInputWrapper>
          );
        }

        return (
          <React.Fragment key={item.key}>
            <FormRadioInputWrapper>
              <ChannelTextRow>
                <item.icon
                  aria-hidden="true"
                  style={{
                    height: ICON_SIZE,
                    width: ICON_SIZE,
                  }}
                />
                <span>{item.staticText}</span>
              </ChannelTextRow>
            </FormRadioInputWrapper>
            {item.showDisclaimer && (
              <p style={{ paddingLeft: '40px' }}>
                Message &amp; data rates may apply. Reply STOP to unsubscribe at any time.
              </p>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
